import { calculateBoundsFromEncodedBoundary as calculateBounds } from '@/src/lib/map/boundaryBoundsCalculator';
import { fitMapCamera } from '@/src/lib/map/fitMapCamera';

/**
 * Pure boundary-to-viewport math. Extracted from AreaBoundary.tsx so it's
 * safe to import from server components (AreaBoundary pulls client hooks).
 */
export type MapPositionFromBoundary = {
  center: { lat: number; lng: number };
  zoom: number;
  bounds: {
    northLat: number;
    southLat: number;
    eastLng: number;
    westLng: number;
  };
};

export type BoundaryBounds = {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
};

export function calculateCenterAndZoomFromBounds(
  bounds: BoundaryBounds,
  mapWidth = 800,
  mapHeight = 600,
  padding = 60,
): MapPositionFromBoundary | null {
  if (
    !Number.isFinite(bounds.minLat) ||
    !Number.isFinite(bounds.maxLat) ||
    !Number.isFinite(bounds.minLng) ||
    !Number.isFinite(bounds.maxLng)
  ) {
    return null;
  }

  const camera = fitMapCamera(
    {
      northLat: bounds.maxLat,
      southLat: bounds.minLat,
      eastLng: bounds.maxLng,
      westLng: bounds.minLng,
    },
    { width: mapWidth, height: mapHeight },
    { top: padding, right: padding, bottom: padding, left: padding },
  );
  if (!camera) return null;

  return {
    center: camera.center,
    zoom: camera.zoom,
    bounds: {
      northLat: bounds.maxLat,
      southLat: bounds.minLat,
      eastLng: bounds.maxLng,
      westLng: bounds.minLng,
    },
  };
}

export function calculateCenterAndZoomFromBoundary(
  encodedBoundary: string | Array<Array<string>>,
  mapWidth = 800,
  mapHeight = 600,
  padding = 60,
): MapPositionFromBoundary | null {
  const bounds = calculateBounds(encodedBoundary);
  if (!bounds) return null;

  return calculateCenterAndZoomFromBounds(bounds, mapWidth, mapHeight, padding);
}
